import { supabase } from "../supabaseClient";
import { PlayerProfile } from "./supabasePlayerProfiles";

export type SquadFormat = "indoor" | "t20" | "odi";

export const SQUAD_FORMATS: { key: SquadFormat; label: string }[] = [
  { key: "indoor", label: "Indoor" },
  { key: "t20", label: "T20" },
  { key: "odi", label: "ODI" },
];

export function matchFormatToSquadFormat(format: string): SquadFormat | null {
  switch (format) {
    case "INDOOR":
      return "indoor";
    case "T20":
      return "t20";
    case "ODI":
      return "odi";
    default:
      return null;
  }
}

export interface Team {
  id: string;
  user_id: string;
  name: string;
  format: SquadFormat;
  logo_url: string | null;
  created_at: string;
  updated_at: string;
}

export interface TeamSquad {
  id: string;
  team_id: string;
  format: SquadFormat;
  captain_player_id: string | null;
  vice_captain_player_id: string | null;
  wicketkeeper_player_id: string | null;
  created_at: string;
  updated_at: string;
}

export type SquadRole = "captain" | "vice_captain" | "wicketkeeper";

const ROLE_COLUMN: Record<SquadRole, keyof TeamSquad> = {
  captain: "captain_player_id",
  vice_captain: "vice_captain_player_id",
  wicketkeeper: "wicketkeeper_player_id",
};

export interface SquadWithPlayers {
  squad: TeamSquad;
  players: PlayerProfile[];
}

export async function listTeams(format?: SquadFormat): Promise<Team[]> {
  let query = supabase
    .from("teams")
    .select("*")
    .order("created_at", { ascending: false });

  if (format) {
    query = query.eq("format", format);
  }

  const { data, error } = await query;

  if (error) {
    console.error("[Teams] Error listing teams:", error);
    throw new Error(error.message);
  }
  return data || [];
}

export async function createTeam(name: string, format: SquadFormat, logoUrl?: string | null): Promise<Team> {
  const trimmed = name.trim();
  if (!trimmed) throw new Error("Team name is required");

  const { data, error } = await supabase
    .from("teams")
    .insert([{ name: trimmed, format, logo_url: logoUrl ?? null }])
    .select("*")
    .single();

  if (error) {
    console.error("[Teams] Error creating team:", error);
    throw new Error(error.message);
  }
  return data;
}

export async function updateTeam(
  id: string,
  updates: { name?: string; logo_url?: string | null }
): Promise<Team> {
  const payload: Record<string, unknown> = { updated_at: new Date().toISOString() };
  if (updates.name !== undefined) {
    const trimmed = updates.name.trim();
    if (!trimmed) throw new Error("Team name is required");
    payload.name = trimmed;
  }
  if (updates.logo_url !== undefined) payload.logo_url = updates.logo_url;

  const { data, error } = await supabase
    .from("teams")
    .update(payload)
    .eq("id", id)
    .select("*")
    .single();

  if (error) {
    console.error("[Teams] Error updating team:", error);
    throw new Error(error.message);
  }
  return data;
}

export async function deleteTeam(id: string): Promise<void> {
  const { error } = await supabase.from("teams").delete().eq("id", id);
  if (error) {
    console.error("[Teams] Error deleting team:", error);
    throw new Error(error.message);
  }
}

export async function ensureSquad(teamId: string, format: SquadFormat): Promise<TeamSquad> {
  const { data: existing, error: findError } = await supabase
    .from("team_squads")
    .select("*")
    .eq("team_id", teamId)
    .eq("format", format)
    .maybeSingle();

  if (findError) {
    console.error("[Teams] Error finding squad:", findError);
    throw new Error(findError.message);
  }
  if (existing) return existing;

  const { data, error } = await supabase
    .from("team_squads")
    .insert([{ team_id: teamId, format }])
    .select("*")
    .single();

  if (error) {
    console.error("[Teams] Error creating squad:", error);
    throw new Error(error.message);
  }
  return data;
}

export async function getSquadWithPlayers(
  teamId: string,
  format: SquadFormat
): Promise<SquadWithPlayers | null> {
  const { data: squad, error: squadError } = await supabase
    .from("team_squads")
    .select("*")
    .eq("team_id", teamId)
    .eq("format", format)
    .maybeSingle();

  if (squadError) {
    console.error("[Teams] Error loading squad:", squadError);
    throw new Error(squadError.message);
  }
  if (!squad) return null;

  const { data: rows, error: playersError } = await supabase
    .from("team_squad_players")
    .select("player_id, player_profiles(*)")
    .eq("squad_id", squad.id)
    .order("created_at", { ascending: true });

  if (playersError) {
    console.error("[Teams] Error loading squad players:", playersError);
    throw new Error(playersError.message);
  }

  const players: PlayerProfile[] = (rows || [])
    .map((r: any) => r.player_profiles)
    .filter((p: PlayerProfile | null): p is PlayerProfile => !!p && !p.is_archived);

  return { squad, players };
}

export async function addPlayerToSquad(squadId: string, playerId: string): Promise<void> {
  const { error } = await supabase
    .from("team_squad_players")
    .insert([{ squad_id: squadId, player_id: playerId }]);

  if (error && error.code !== "23505") {
    console.error("[Teams] Error adding player to squad:", error);
    throw new Error(error.message);
  }
}

export async function removePlayerFromSquad(squadId: string, playerId: string): Promise<void> {
  const { error } = await supabase
    .from("team_squad_players")
    .delete()
    .eq("squad_id", squadId)
    .eq("player_id", playerId);

  if (error) {
    console.error("[Teams] Error removing player from squad:", error);
    throw new Error(error.message);
  }
}

export async function setSquadRole(
  squadId: string,
  role: SquadRole,
  playerId: string | null
): Promise<TeamSquad> {
  const column = ROLE_COLUMN[role];
  const { data, error } = await supabase
    .from("team_squads")
    .update({ [column]: playerId, updated_at: new Date().toISOString() })
    .eq("id", squadId)
    .select("*")
    .single();

  if (error) {
    console.error("[Teams] Error setting squad role:", error);
    throw new Error(error.message);
  }
  return data;
}
